// src/Pages/Invoices/invoicecomps/bcc/BBCSplitInvoiceDesign.jsx
//
// Rendered by InvoicePage when invoice_type === 'bbc'.
//
// Design changes from previous version
// ─────────────────────────────────────
// • White page background — no purple gradient shell. The invoice Paper
//   sits on a neutral #f5f6f8 bg, identical to how an AP team expects
//   to see a document.
// • Yellow (#FFD600) is used structurally only: 4px top bar, amount-due
//   row accent, ⚡ chips. Everything else is slate/white/grey.
// • Contractor roster is a collapsible clean table — no color-flooded rows.
// • Actions live in a sticky bottom action bar (not a separate card
//   below the fold). The bar collapses on scroll-up via IntersectionObserver.
// • Identity gate is wired in via BBCSplitIdentityGate (separate file).
// • The printable #invoice-content Paper is self-contained and action-free.

import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box, Typography, Paper, Fade, Grid, Stack, Avatar, Divider,
  Toolbar, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Alert, Button, Collapse, CircularProgress,
  IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import {
  Bolt as BoltIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FlashOn as FlashOnIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import HomeHeader from '../../../../Components/HomeHeader';
import HomeFooter from '../../../../Components/HomeFooter';
import { professionalTheme } from '../invoicecomps/themes';
import BBCSplitIdentityGate from './BlitzSplitIdentiyGate';
import client from '../../../../API';

// ── Design tokens ─────────────────────────────────────────────────── //
const Y       = '#FFD600';
const Y_DARK  = '#F5C800';
const Y_INK   = '#1A1200';
const Y_TINT  = alpha('#FFD600', 0.07);
const Y_BDR   = alpha('#FFD600', 0.22);
const Y_DEEP  = '#8C6D00';
const SLATE   = '#1e293b';
const MUTED   = '#64748b';
const PAGE_BG = '#f5f6f8';

const usd = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n ?? 0);

const termLabel = (days) => {
  if (days == null || days === '') return 'Standard terms';
  if (Number(days) === 0) return 'Due on receipt';
  return `Net ${days}`;
};

// ── Tiny label/value pair ─────────────────────────────────────────── //
const Field = ({ label, value, mono = false }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        color: MUTED,
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        fontSize: '0.6rem',
        mb: 0.3,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      sx={{
        color: SLATE,
        fontFamily: mono ? 'monospace' : 'inherit',
        fontSize: mono ? '0.82rem' : '0.875rem',
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

// ── Roster table row ──────────────────────────────────────────────── //
const RosterRow = ({ payment, index }) => {
  const name =
    payment.recipient_name ||
    payment.creator_id     ||
    payment.recipient_email ||
    `Contractor ${index + 1}`;
  const amt  = parseFloat(payment.amount || 0);
  const pre  = payment.preapproved || payment.blitzpay_preapproved || payment.blitzpay;
  const status = (payment.status || 'promised').toLowerCase();

  return (
    <TableRow
      sx={{
        '&:last-child td': { border: 0 },
        '&:hover': { bgcolor: '#fafafa' },
      }}
    >
      <TableCell sx={{ py: 1.25, pl: 2.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              width: 26, height: 26,
              fontSize: '0.65rem', fontWeight: 700,
              bgcolor: pre ? Y : '#f1f5f9',
              color: pre ? Y_INK : MUTED,
              flexShrink: 0,
            }}
          >
            {name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ fontSize: '0.82rem', color: SLATE }}
            >
              {name}
            </Typography>
            {payment.recipient_email && payment.recipient_email !== name && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: '0.62rem', display: 'block' }}
                noWrap
              >
                {payment.recipient_email}
              </Typography>
            )}
          </Box>
        </Stack>
      </TableCell>

      <TableCell
        align="right"
        sx={{ py: 1.25, fontVariantNumeric: 'tabular-nums', color: SLATE }}
      >
        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>
          {usd(amt)}
        </Typography>
      </TableCell>

      <TableCell sx={{ py: 1.25, pr: 2.5 }}>
        {pre ? (
          <Chip
            label="⚡ Instant"
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 700,
              bgcolor: Y,
              color: Y_INK,
              border: 'none',
            }}
          />
        ) : (
          <Typography
            variant="caption"
            sx={{ color: MUTED, fontSize: '0.7rem' }}
          >
            {status === 'promised' ? 'At funding' : status}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

// ── Sticky action bar ─────────────────────────────────────────────── //
const StickyActionBar = ({
  invoice,
  bbcSummary,
  creatorPayments,
  actionLoading,
  actionMode,
  onApprove,
  onDecline,
  onConvert,
  alreadyDone,
  confirmed,
}) => {
  const [balance, setBalance]       = useState(null);
  const [balLoading, setBalLoading] = useState(true);
  const [visible, setVisible]       = useState(true);
  const sentinelRef                 = useRef(null);

  useEffect(() => {
    client.balanceLedger.fetchMyBalance()
      .then((d) => setBalance(parseFloat(d?.balance ?? 0)))
      .catch(() => setBalance(null))
      .finally(() => setBalLoading(false));
  }, []);

  const remaining    = creatorPayments.filter((p) => p.status === 'promised');
  const grossRemain  = remaining.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const feeRate      = parseFloat(bbcSummary?.instant_fee_rate || 5);
  const fee          = Math.round(grossRemain * feeRate) / 100;
  const canAfford    = balance !== null && balance >= fee;
  const hasRemaining = remaining.length > 0;
  const bbcId        = bbcSummary?.bbc_id;

  if (alreadyDone || confirmed) return null;

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
        bgcolor: 'white',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.07)',
        px: { xs: 2, sm: 4 },
        py: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: '210mm',
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        {/* Instant-convert option */}
        {hasRemaining && bbcId && (
          <Tooltip
            title={
              !canAfford && !balLoading
                ? `Need ${usd(fee - (balance || 0))} more in Blitz balance`
                : `Pay ${usd(fee)} (${feeRate}% of ${usd(grossRemain)}) from your balance. All ${remaining.length} contractors credited now.`
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                size="medium"
                disabled={!canAfford || actionLoading || balLoading}
                onClick={onConvert}
                startIcon={
                  actionLoading && actionMode === 'convert'
                    ? <CircularProgress size={14} sx={{ color: Y_INK }} />
                    : <FlashOnIcon />
                }
                sx={{
                  bgcolor: Y,
                  color: Y_INK,
                  fontWeight: 800,
                  textTransform: 'none',
                  borderRadius: 2.5,
                  px: 2.5,
                  py: 1,
                  fontSize: '0.82rem',
                  flexShrink: 0,
                  boxShadow: canAfford ? `0 2px 12px ${alpha(Y, 0.45)}` : 'none',
                  '&:hover': { bgcolor: Y_DARK },
                  '&.Mui-disabled': {
                    bgcolor: alpha(Y, 0.2),
                    color: alpha(Y_INK, 0.35),
                  },
                }}
              >
                {actionLoading && actionMode === 'convert'
                  ? 'Converting…'
                  : `⚡ Instant pay — ${usd(fee)} fee`}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Approve at funding */}
        <Button
          variant="outlined"
          size="medium"
          disabled={actionLoading}
          onClick={onApprove}
          startIcon={
            actionLoading && actionMode === 'approve'
              ? <CircularProgress size={14} color="inherit" />
              : <CheckCircleIcon />
          }
          sx={{
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 2.5,
            px: 2.5,
            py: 1,
            fontSize: '0.82rem',
            flexShrink: 0,
            borderColor: alpha(SLATE, 0.25),
            color: SLATE,
            '&:hover': {
              borderColor: SLATE,
              bgcolor: '#f8f9fb',
            },
          }}
        >
          {actionLoading && actionMode === 'approve'
            ? 'Approving…'
            : hasRemaining
              ? 'Approve — pay at funding'
              : 'Approve'}
        </Button>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Decline */}
        <Button
          variant="text"
          color="error"
          size="medium"
          disabled={actionLoading}
          onClick={onDecline}
          startIcon={
            actionLoading && actionMode === 'decline'
              ? <CircularProgress size={14} color="inherit" />
              : <CancelIcon />
          }
          sx={{
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 2.5,
            px: 2,
            py: 1,
            fontSize: '0.82rem',
            flexShrink: 0,
          }}
        >
          Decline
        </Button>
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════
const BBCSplitInvoiceDesign = ({
  invoice,
  user,
  company,
  bbcSummary,
  creatorPayments = [],
  billedToName,
  billedToEmail,
  onDownloadPDF,
  onCopyInvoiceNumber,
  onShareInvoice,
  pdfGenerating,
  formatDate,
  getStatusColor,
  getStatusText,
  getEffectiveValue,
  daysLate,
  calculatedDueDate,
  needsApproval,
  userInfo,
  onOpenLoginDialog,
  isAuthenticated = false,
  isCreatorAuthorized = false,
}) => {
  const [rosterOpen,    setRosterOpen]    = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMode,    setActionMode]    = useState(null);
  const [actionError,   setActionError]   = useState(null);
  const [confirmed,     setConfirmed]     = useState(null);

  const invoiceStatus = (invoice?.status || invoice?.invoice_status || '').toLowerCase();
  const alreadyDone   = ['approved','paid','funded','cancelled','declined'].includes(invoiceStatus);

  const total      = parseFloat(invoice?.amount_due || invoice?.amount || 0);
  const preCount   = creatorPayments.filter((p) => p.preapproved || p.blitzpay_preapproved || p.blitzpay).length;
  const totalCount = creatorPayments.length;

  const invoiceNumber = getEffectiveValue('invoice_number', invoice?.po_number);
  const dateIssued    = getEffectiveValue('date_of_issue', invoice?.created_at);
  const senderName    = bbcSummary?.enterprise_name || invoice?.client_name || 'Agency';

  // ── Action handlers ─────────────────────────────────────────────── //
  const doAction = async (mode) => {
    setActionLoading(true);
    setActionMode(mode);
    setActionError(null);
    try {
      if (mode === 'convert' && bbcSummary?.bbc_id) {
        await client.bbc.convertToBlitzPay(bbcSummary.bbc_id);
      }
      await client.invoices.unifiedAction({
        invoiceId: invoice.id,
        action: 'approve',
      });
      setConfirmed(mode === 'convert' ? 'approved_instant' : 'approved');
    } catch (err) {
      setActionError(
        err?.response?.data?.error ||
        (mode === 'convert'
          ? 'Conversion failed. Check your Blitz balance and try again.'
          : 'Could not approve invoice. Please try again.'),
      );
    } finally {
      setActionLoading(false);
      setActionMode(null);
    }
  };

  const doDecline = async () => {
    setActionLoading(true);
    setActionMode('decline');
    setActionError(null);
    try {
      await client.invoices.unifiedAction({
        invoiceId: invoice.id,
        action: 'decline',
      });
      setConfirmed('declined');
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Could not decline invoice.');
    } finally {
      setActionLoading(false);
      setActionMode(null);
    }
  };

  // ── Logout helper ─────────────────────────────────────────────────── //
  const handleLogout = () => {
    // Clear tokens and reload — caller can override if preferred
    localStorage.removeItem('jwt');
    sessionStorage.clear();
    window.location.reload();
  };

  return (
    <ThemeProvider theme={professionalTheme}>
      {/* Identity gate wraps everything */}
      <BBCSplitIdentityGate
        userInfo={userInfo}
        invoice={invoice}
        isCreatorAuthorized={isCreatorAuthorized}
        onLogout={handleLogout}
      >
        <Box
          sx={{
            minHeight: '100vh',
            bgcolor: PAGE_BG,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <HomeHeader />
          <Toolbar />

          {/* ── Top utility bar (download / copy / share) ──────────── */}
          <Box
            sx={{
              bgcolor: 'white',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: { xs: 2, sm: 4 },
              py: 1.25,
              position: 'sticky',
              top: 64,
              zIndex: 100,
            }}
          >
            <Box
              sx={{
                maxWidth: '210mm',
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              {/* Status pill */}
              <Chip
                label={getStatusText(invoiceStatus)}
                color={getStatusColor(invoiceStatus)}
                size="small"
                sx={{ fontWeight: 700, fontSize: '0.7rem', mr: 'auto' }}
              />

              <Tooltip title="Copy invoice number">
                <IconButton size="small" onClick={onCopyInvoiceNumber}>
                  <CopyIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>

              {onShareInvoice && (
                <Tooltip title="Share invoice link">
                  <IconButton size="small" onClick={onShareInvoice}>
                    <ShareIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}

              <Button
                variant="outlined"
                size="small"
                onClick={onDownloadPDF}
                disabled={pdfGenerating}
                startIcon={
                  pdfGenerating
                    ? <CircularProgress size={12} color="inherit" />
                    : <DownloadIcon sx={{ fontSize: '15px !important' }} />
                }
                sx={{
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  fontSize: '0.75rem',
                  ml: 0.5,
                  px: 1.75,
                  borderColor: alpha(SLATE, 0.2),
                  color: SLATE,
                  '&:hover': { borderColor: SLATE, bgcolor: 'transparent' },
                }}
              >
                {pdfGenerating ? 'Generating…' : 'Download PDF'}
              </Button>
            </Box>
          </Box>

          {/* ── Confirmation banners (shown after action) ──────────── */}
          {confirmed && (
            <Fade in timeout={400}>
              <Box
                sx={{
                  width: { xs: '95%', sm: '210mm' },
                  maxWidth: '100%',
                  mx: 'auto',
                  mt: 2.5,
                }}
              >
                {confirmed === 'approved_instant' && (
                  <Alert
                    severity="success"
                    icon={<BoltIcon />}
                    sx={{ borderRadius: 3, fontWeight: 600 }}
                  >
                    Split approved and converted to BlitzPay ⚡ — all contractors credited instantly.
                  </Alert>
                )}
                {confirmed === 'approved' && (
                  <Alert severity="success" sx={{ borderRadius: 3, fontWeight: 600 }}>
                    Split approved — contractors will be credited when your payment lands.
                  </Alert>
                )}
                {confirmed === 'declined' && (
                  <Alert severity="info" sx={{ borderRadius: 3, fontWeight: 600 }}>
                    Invoice declined. {senderName} has been notified.
                  </Alert>
                )}
              </Box>
            </Fade>
          )}

          {/* ── Needs-approval banner ──────────────────────────────── */}
          {needsApproval && !alreadyDone && !confirmed && (
            <Box
              sx={{
                width: { xs: '95%', sm: '210mm' },
                maxWidth: '100%',
                mx: 'auto',
                mt: 2.5,
              }}
            >
              <Alert
                icon={<BoltIcon sx={{ color: Y_DEEP }} />}
                sx={{
                  borderRadius: 3,
                  bgcolor: Y_TINT,
                  border: `1px solid ${Y_BDR}`,
                  '& .MuiAlert-icon': { alignItems: 'center', color: Y_DEEP },
                }}
              >
                <Typography variant="body2" fontWeight={700} sx={{ color: SLATE }}>
                  This invoice is awaiting your approval
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {senderName} is requesting {usd(total)} for {totalCount} contractor{totalCount !== 1 ? 's' : ''}.
                  {preCount > 0 && ` ${preCount} have elected instant BlitzPay ⚡.`}
                  {' '}Use the action bar below to approve or decline.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* ── Error from actions ──────────────────────────────────── */}
          {actionError && (
            <Box
              sx={{
                width: { xs: '95%', sm: '210mm' },
                maxWidth: '100%',
                mx: 'auto',
                mt: 2,
              }}
            >
              <Alert
                severity="error"
                icon={<WarningIcon />}
                onClose={() => setActionError(null)}
                sx={{ borderRadius: 3 }}
              >
                {actionError}
              </Alert>
            </Box>
          )}

          {/* ══════════════════════════════════════════════════════════
              INVOICE PAPER — printable area
              ══════════════════════════════════════════════════════════ */}
          <Box sx={{ py: 3, flex: 1 }}>
            <Paper
              id="invoice-content"
              elevation={0}
              sx={{
                width: { xs: '95%', sm: '210mm' },
                maxWidth: '100%',
                minHeight: { xs: 'auto', sm: '297mm' },
                mx: 'auto',
                background: 'white',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {/* Yellow top accent */}
              <Box
                sx={{
                  height: 4,
                  background: `linear-gradient(90deg, ${Y} 0%, ${alpha(Y, 0.3)} 100%)`,
                }}
              />

              <Box sx={{ p: { xs: 3, sm: 5 } }}>
                {/* ── HEADER ──────────────────────────────────────── */}
                <Grid container spacing={3} sx={{ mb: 4 }} alignItems="flex-start">
                  {/* From */}
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar
                        sx={{ width: 36, height: 36, bgcolor: Y, color: Y_INK, flexShrink: 0 }}
                      >
                        <BoltIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: MUTED, fontSize: '0.6rem', letterSpacing: '0.1em', lineHeight: 1 }}
                        >
                          Blitz Split Invoice
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={800}
                          sx={{ color: SLATE, lineHeight: 1.1 }}
                        >
                          {senderName}
                        </Typography>
                      </Box>
                    </Stack>
                    {invoice?.client_email && (
                      <Typography
                        variant="caption"
                        sx={{ color: MUTED, fontSize: '0.75rem' }}
                      >
                        {invoice.client_email}
                      </Typography>
                    )}
                  </Grid>

                  {/* Amount due */}
                  <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: Y_DEEP,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontSize: '0.6rem',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Amount due (USD)
                    </Typography>
                    <Typography
                      variant="h3"
                      fontWeight={900}
                      sx={{
                        color: SLATE,
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1,
                      }}
                    >
                      {usd(total)}
                    </Typography>
                    {daysLate > 0 && (
                      <Chip
                        label={`${daysLate} days overdue`}
                        color="error"
                        size="small"
                        sx={{ mt: 1, fontWeight: 700, fontSize: '0.68rem' }}
                      />
                    )}
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 3.5 }} />

                {/* ── Invoice meta fields ──────────────────────────── */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={4}>
                    <Field label="Billed To" value={billedToName || '—'} />
                    {billedToEmail && (
                      <Typography
                        variant="caption"
                        sx={{ color: MUTED, display: 'block', mt: 0.5, fontSize: '0.72rem' }}
                      >
                        {billedToEmail}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6} sm={2.5}>
                    <Field label="Date issued" value={formatDate(dateIssued)} />
                  </Grid>
                  <Grid item xs={6} sm={2.5}>
                    <Field label="Due date" value={calculatedDueDate ? formatDate(calculatedDueDate) : termLabel(invoice?.payment_term)} />
                  </Grid>
                  <Grid item xs={6} sm={1.5}>
                    <Field label="Terms" value={termLabel(invoice?.payment_term)} />
                  </Grid>
                  <Grid item xs={6} sm={1.5}>
                    <Field label="PO / Ref" value={invoiceNumber} mono />
                  </Grid>
                  {bbcSummary?.bbc_id && (
                    <Grid item xs={12} sm={12}>
                      <Field label="Split ID" value={`BBC-${bbcSummary.bbc_id}`} mono />
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ mb: 3.5, borderColor: Y_BDR }} />

                {/* ── Contractor roster ───────────────────────────── */}
                <Box sx={{ mb: 4 }}>
                  {/* Roster header — always visible */}
                  <Box
                    onClick={() => setRosterOpen(!rosterOpen)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      py: 1,
                      px: 0.25,
                      borderRadius: 1.5,
                      userSelect: 'none',
                      '&:hover': { bgcolor: '#f8f9fb' },
                      transition: 'background 0.12s',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: MUTED,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          fontSize: '0.6rem',
                        }}
                      >
                        Contractor Roster
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: SLATE, fontWeight: 600, fontSize: '0.82rem' }}
                      >
                        {totalCount} contractor{totalCount !== 1 ? 's' : ''}
                        {preCount > 0 && (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              ml: 1,
                              bgcolor: Y,
                              color: Y_INK,
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              px: 0.75,
                              py: 0.15,
                              borderRadius: 1,
                            }}
                          >
                            {preCount} ⚡ instant
                          </Box>
                        )}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{ color: Y_DEEP, fontWeight: 700, fontSize: '0.7rem' }}
                      >
                        {rosterOpen ? 'Collapse' : `View ${totalCount}`}
                      </Typography>
                      {rosterOpen
                        ? <ExpandLessIcon sx={{ fontSize: 16, color: Y_DEEP }} />
                        : <ExpandMoreIcon sx={{ fontSize: 16, color: Y_DEEP }} />}
                    </Stack>
                  </Box>

                  {/* Proportion bar (collapsed preview) */}
                  {!rosterOpen && totalCount > 0 && (
                    <Box
                      sx={{
                        mt: 0.75,
                        height: 5,
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        gap: '2px',
                        bgcolor: '#f1f5f9',
                      }}
                    >
                      {creatorPayments.map((p, i) => (
                        <Box
                          key={p.id || i}
                          sx={{
                            flex: parseFloat(p.amount || 1),
                            bgcolor: (p.preapproved || p.blitzpay_preapproved || p.blitzpay)
                              ? Y
                              : alpha(SLATE, 0.18),
                            borderRadius: 2,
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Roster table */}
                  <Collapse in={rosterOpen}>
                    <TableContainer
                      sx={{
                        mt: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: MUTED,
                                fontSize: '0.65rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                py: 1.25,
                                pl: 2.5,
                              }}
                            >
                              Contractor
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: MUTED,
                                fontSize: '0.65rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                py: 1.25,
                              }}
                            >
                              Amount
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: MUTED,
                                fontSize: '0.65rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                py: 1.25,
                                pr: 2.5,
                              }}
                            >
                              Settlement
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {creatorPayments.map((p, i) => (
                            <RosterRow key={p.id || i} payment={p} index={i} />
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Subtotals */}
                    {bbcSummary && (
                      <Box sx={{ mt: 2, px: 1 }}>
                        <Stack spacing={0.5}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              Contractor subtotal
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {usd(bbcSummary.creators_subtotal)}
                            </Typography>
                          </Stack>
                          {parseFloat(bbcSummary.sme_margin || 0) > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption" color="text.secondary">
                                Agency management fee
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                sx={{ fontVariantNumeric: 'tabular-nums' }}
                              >
                                {usd(bbcSummary.sme_margin)}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Collapse>
                </Box>

                {/* ── Line-item table ──────────────────────────────── */}
                <TableContainer sx={{ mb: 4 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: '#f8f9fb' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: MUTED, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', py: 1.25 }}>
                          Description
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: MUTED, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', py: 1.25, width: 60 }}>
                          Qty
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: MUTED, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', py: 1.25 }}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ color: SLATE, fontWeight: 600, mb: 0.3 }}
                          >
                            Blitz Split — multi-contractor disbursement
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: MUTED, lineHeight: 1.5 }}
                          >
                            {totalCount} contractor{totalCount !== 1 ? 's' : ''} ·{' '}
                            {termLabel(invoice?.payment_term)}
                            {preCount > 0 && ` · ${preCount} elected instant BlitzPay ⚡`}
                            {invoiceNumber && ` · PO ${invoiceNumber}`}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ py: 2, color: MUTED, fontSize: '0.875rem' }}
                        >
                          1
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            py: 2,
                            fontWeight: 800,
                            fontVariantNumeric: 'tabular-nums',
                            color: SLATE,
                          }}
                        >
                          {usd(total)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* ── Totals block ────────────────────────────────── */}
                <Grid container justifyContent="flex-end" sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={5}>
                    <Stack spacing={0.6}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {usd(total)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Tax</Typography>
                        <Typography variant="body2">$0.00</Typography>
                      </Stack>
                      <Divider sx={{ my: 0.5 }} />
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          bgcolor: Y_TINT,
                          border: `1px solid ${Y_BDR}`,
                          borderRadius: 1.5,
                          px: 1.25,
                          py: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{ color: Y_DEEP }}
                        >
                          Amount Due (USD)
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={900}
                          sx={{
                            color: Y_DEEP,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {usd(total)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 3, borderColor: Y_BDR }} />

                {/* ── Payment terms + footer ───────────────────────── */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: MUTED,
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      fontSize: '0.6rem',
                      display: 'block',
                      mb: 0.75,
                    }}
                  >
                    Terms &amp; payment instructions
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: MUTED, display: 'block', lineHeight: 1.8, fontSize: '0.75rem' }}
                  >
                    Payment due {termLabel(invoice?.payment_term)} from invoice date.
                    This invoice covers {totalCount} independent contractor payment
                    {totalCount !== 1 ? 's' : ''} coordinated via Blitz Systems Corp.
                    {preCount > 0 && (
                      ` ${preCount} contractor${preCount !== 1 ? 's have' : ' has'} elected instant BlitzPay — the early-payout fee (${bbcSummary?.instant_fee_rate ?? 5}%) is deducted from their individual slice, not from this invoice total.`
                    )}
                    {' '}Wire payment to Blitz Systems Corp · Prosperity Bank · Routing 113122655.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    pt: 2.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ color: MUTED, fontSize: '0.7rem' }}>
                    Questions? win@useblitz.co
                  </Typography>
                  <Typography variant="caption" sx={{ color: MUTED, fontSize: '0.7rem' }}>
                    Blitz Systems Corp · Houston, TX 77055
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          <HomeFooter />

          {/* ── Sticky action bar ─────────────────────────────────── */}
          {userInfo && !confirmed && (
            <StickyActionBar
              invoice={invoice}
              bbcSummary={bbcSummary}
              creatorPayments={creatorPayments}
              actionLoading={actionLoading}
              actionMode={actionMode}
              onApprove={() => doAction('approve')}
              onDecline={doDecline}
              onConvert={() => doAction('convert')}
              alreadyDone={alreadyDone}
              confirmed={confirmed}
            />
          )}
        </Box>
      </BBCSplitIdentityGate>
    </ThemeProvider>
  );
};

export default BBCSplitInvoiceDesign;